import { ref, computed, onMounted, watch, provide } from "vue";
import L from "leaflet";
import { useStore } from "vuex";

const DEFAULT_ZOOM = 15.5;
const DEFAULT_CENTER = [51.505117019117044, -0.15981318574761885];

export function useRangePinsView() {
  const store = useStore();

  const courses = computed(() => store.getters["courses/getCourseNames"]);
  const rangePins = computed(
    () => store.getters["drivingRangePins/getRangePins"] ?? []
  );
  const drivingRangeSetup = computed(
    () => store.getters["drivingRangePins/getDrivingRangeSetup"]
  );
  const loading = computed(() => store.getters["drivingRangePins/getLoading"]);
  const selectedCourse = computed(
    () => store.getters["drivingRangePins/getSelectedCourse"]
  );
  const targetCourse = ref(null);

  provide("targetCourse", targetCourse);

  const showImportModal = ref(false);
  const showClearModal = ref(false);

  const openImportRangePins = () => {
    showImportModal.value = true;
  };

  const openClearRangePins = () => {
    showClearModal.value = true;
  };

  const confirmClearRangePins = async () => {
    if (targetCourse.value) {
      try {
        await store.dispatch(
          "drivingRangePins/clearRangePins",
          targetCourse.value.id
        );
        showClearModal.value = false;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const myMap = ref(null);
  const showCourseMap = ref(true);

  const zoom = ref(DEFAULT_ZOOM);
  const center = ref([...DEFAULT_CENTER]);
  const url = ref(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
  );
  const enableSaveRangePins = ref(false);
  const opacity = ref(0.6);
  const mapImage = ref("");
  const bounds = ref([
    [0, 0],
    [0, 0],
  ]);
  const icon = ref(
    L.icon({
      iconUrl: "/images/dot-circle.svg",
      iconSize: [12, 12],
    })
  );

  const centerUpdated = (newCenter) => {
    center.value = newCenter;
  };

  const changeView = (satellite) => {
    showCourseMap.value = !satellite;
  };

  const setCourse = (course) => {
    store.commit("drivingRangePins/SET_SELECTED_COURSE", course);
  };

  const updatePin = (index, latlng) => {
    const updatedPins = [...rangePins.value];
    if (!updatedPins[index]) return;

    updatedPins[index] = {
      ...updatedPins[index],
      point: [latlng.lat, latlng.lng],
    };
    store.commit("drivingRangePins/SET_RANGE_PINS", updatedPins);
    enableSaveRangePins.value = true;
  };

  const updateRangePins = async () => {
    if (!targetCourse.value) return;

    try {
      await store.dispatch("drivingRangePins/updateDrivingRangePins", {
        courseId: targetCourse.value.id,
        rangePins: rangePins.value,
      });
      // Only lock the button once the update actually succeeded, so a failed
      // save can be retried without dragging the pins again.
      enableSaveRangePins.value = false;
    } catch (error) {
      // The failure is already surfaced by the toast in the action.
    }
  };

  const applyMapView = () => {
    const setup = drivingRangeSetup.value;
    if (!setup || !targetCourse.value) return;

    const northEastLatitude = parseFloat(setup.drivingRangeNorthEastLatitude);
    const northEastLongitude = parseFloat(setup.drivingRangeNorthEastLongitude);
    const southWestLatitude = parseFloat(setup.drivingRangeSouthWestLatitude);
    const southWestLongitude = parseFloat(setup.drivingRangeSouthWestLongitude);

    const hasBounds = [
      northEastLatitude,
      northEastLongitude,
      southWestLatitude,
      southWestLongitude,
    ].every((value) => !Number.isNaN(value));

    mapImage.value = setup.drivingRangeMap || "";

    const parsedZoom = parseFloat(setup.drivingRangeZoom);
    if (!Number.isNaN(parsedZoom)) {
      zoom.value = parsedZoom;
    }

    if (!hasBounds) return;

    bounds.value = [
      [southWestLatitude, southWestLongitude],
      [northEastLatitude, northEastLongitude],
    ];

    center.value = [
      (northEastLatitude + southWestLatitude) / 2,
      (northEastLongitude + southWestLongitude) / 2,
    ];

    if (myMap.value && myMap.value.leafletObject) {
      myMap.value.leafletObject.fitBounds(bounds.value);
    }
  };

  const resetToDefault = () => {
    zoom.value = DEFAULT_ZOOM;
    center.value = [...DEFAULT_CENTER];
    mapImage.value = "";
    bounds.value = [
      [0, 0],
      [0, 0],
    ];
  };

  watch(
    () => targetCourse.value,
    async (newVal) => {
      enableSaveRangePins.value = false;
      if (newVal) {
        setCourse(newVal.id);
        try {
          store.commit("drivingRangePins/SET_LOADING", true);
          await store.dispatch("drivingRangePins/fetchPwaSetup", newVal.id);
          applyMapView();
        } catch (error) {
          resetToDefault();
        } finally {
          store.commit("drivingRangePins/SET_LOADING", false);
        }
      } else {
        setCourse(null);
        resetToDefault();
        store.commit("drivingRangePins/SET_DRIVING_RANGE_SETUP", null);
        store.commit("drivingRangePins/SET_RANGE_PINS", []);
      }
    }
  );

  onMounted(() => {
    center.value = [...DEFAULT_CENTER];
    store.dispatch("courses/fetchCourseNames");
  });

  return {
    myMap,
    selectedCourse,
    rangePins,
    loading,
    showCourseMap,
    zoom,
    center,
    url,
    opacity,
    mapImage,
    bounds,
    icon,
    targetCourse,
    drivingRangeSetup,
    courses,
    showImportModal,
    showClearModal,
    enableSaveRangePins,
    openImportRangePins,
    openClearRangePins,
    confirmClearRangePins,
    centerUpdated,
    changeView,
    updatePin,
    updateRangePins,
    applyMapView,
  };
}
